import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Customer, InvoiceConfig, Quote, QuoteConfig } from '../../types';
import { generateQuotePDF, applyOriginalLv, lvOriginalMode, pdfBytesToBase64, pdfBytesToBlob } from '../../services/pdfService';
import { buildCompanyForPdf } from '../../services/pdf/companyForPdf';
import { buildQuoteEml, downloadEml } from '../../services/emlBuilder';
import { uploadPdf } from '../../services/cloudService';
import { markQuoteSent } from '../../services/crmService';
import { computeFollowUpDate, portalSentTo } from '../../services/quoteLogic';
import { rechtFehlerOder } from '../../services/rechte/rechtFehler';
import { dateiAusgeben } from '../../services/dateiAusgabe';
import { Button } from '../Button';
import { Mail, X, Loader2, CheckCircle2, Download, Info, Upload } from 'lucide-react';

interface Props {
  quote: Quote;
  customer: Customer;
  quoteConfig?: QuoteConfig;
  /** Rechnungs-Stammdaten — speisen die Spalten-Fusszeile, wie auf der Rechnung. */
  invoiceConfig?: InvoiceConfig;
  company: { name?: string; address?: string; logo?: string; footerText?: string; senderLine?: string };
  // Ersteller-Name fuer den Grussblock im PDF (z. B. "Ronny Ehlers").
  userName?: string;
  // Persoenliche Unterschrift (dataURL) — im PDF ueber dem Namen.
  userSignature?: string;
  onClose: () => void;
  onMarkedSent: (updated: Partial<Quote>) => void;
}

const fmtDate = (v?: string) => (v ? new Date(v).toLocaleDateString('de-DE') : '');

export const QuoteSendModal: React.FC<Props> = ({ quote, customer, quoteConfig, invoiceConfig, company, userName, userSignature, onClose, onMarkedSent }) => {
  // Empfaenger-Kandidaten: Kontakte mit E-Mail + Firmen-E-Mail als Fallback.
  const candidates = useMemo(() => {
    const list: { label: string; email: string }[] = [];
    (customer.contacts || []).forEach((c) => {
      if (c.email) list.push({ label: `${c.name}${c.role ? ` · ${c.role}` : ''}`, email: c.email });
    });
    if (customer.email && !list.some((x) => x.email === customer.email)) {
      list.push({ label: `${customer.name} (Firma)`, email: customer.email });
    }
    return list;
  }, [customer]);

  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(candidates.length > 0 ? [candidates[0].email] : []),
  );

  // Zwei Wege: Outlook-Entwurf oder PDF fuer das Vergabeportal — dort wird
  // das Angebot hochgeladen, eine Mail geht nicht raus. Ohne hinterlegte
  // E-Mail-Adresse bleibt nur das Portal, der Dialog startet dann dort.
  const [way, setWay] = useState<'mail' | 'portal'>(() => (candidates.length > 0 ? 'mail' : 'portal'));
  const [portalName, setPortalName] = useState('');

  const defaultSubject = `Angebot ${quote.quoteNumber} – ${quote.title || customer.name}`;
  const [subject, setSubject] = useState(defaultSubject);

  // Privatperson mit gepflegter Anrede direkt ansprechen (Titel vor den Namen).
  // Im Brief steht der NACHNAME allein ("Sehr geehrter Herr Dr. Dörfler") —
  // der Vollname mit Vornamen waere in einem Geschaeftsbrief unueblich. Fehlt
  // der Nachname (Altbestand ohne Namenstrennung), bleibt es beim Vollnamen.
  // Ohne Anrede bleibt es beim Ansprechpartner bzw. der Standardformel — eine
  // geratene Anrede ist schlimmer als die neutrale.
  const contactName = customer.contacts?.[0]?.name;
  const personName = [customer.title, (customer.lastName ?? '').trim() || customer.name]
    .filter(Boolean)
    .join(' ');
  const anrede =
    customer.customerType === 'person' && customer.salutation
      ? `Sehr geehrte${customer.salutation === 'Herr' ? 'r' : ''} ${customer.salutation} ${personName},`
      : contactName
        ? `Sehr geehrte/r ${contactName},`
        : 'Sehr geehrte Damen und Herren,';
  const validText = quote.validUntil ? ` Das Angebot ist gültig bis zum ${fmtDate(quote.validUntil)}.` : '';
  const defaultBody =
    `${anrede}\n\n` +
    `anbei erhalten Sie unser Angebot ${quote.quoteNumber} als PDF.${validText}\n\n` +
    `Für Rückfragen stehen wir Ihnen jederzeit gerne zur Verfügung.`;
  const [body, setBody] = useState(defaultBody);

  const [busy, setBusy] = useState(false);
  const [marking, setMarking] = useState(false);
  const [step, setStep] = useState<'compose' | 'done'>('compose');
  const [pdfUrl, setPdfUrl] = useState<string | undefined>(undefined);

  // Die Entwurfserstellung hat jetzt eine laengere async Phase (Original-LV aus
  // dem Bucket laden + mergen, danach Archiv-Upload). Deshalb zwei Guards:
  //  - running: harter Reentrancy-Lock. `disabled={busy}` am Button greift erst
  //    nach dem naechsten Render und schuetzt nicht gegen reentrante Aufrufe —
  //    sonst zwei Uploads und zwei .eml-Downloads.
  //  - alive: das Modal laesst sich waehrend des Laufs schliessen. Danach keine
  //    State-Updates und kein Download mehr auf einer unmounteten Instanz.
  const runningRef = useRef(false);
  const aliveRef = useRef(true);
  useEffect(() => () => { aliveRef.current = false; }, []);

  const recipients = candidates.filter((c) => selected.has(c.email)).map((c) => c.email);
  const sentTo = way === 'portal' ? portalSentTo(portalName) : recipients.join(', ');

  const toggle = (email: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  };

  // Dateiname: Angebotsnummer_Projektname_KD_Kundennummer
  // (z. B. "A-440-2026_Hotel_Holstein_KD_01010"; Sonderzeichen → _).
  const fileBase = [
    quote.quoteNumber,
    quote.title,
    customer.customerNumber ? `KD_${customer.customerNumber}` : '',
  ]
    .filter(Boolean)
    .join('_')
    .replace(/[^\wäöüÄÖÜß-]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');
  const filename = `${fileBase || `Angebot_${quote.quoteNumber}`}.pdf`;

  const handleCreate = async () => {
    if (way === 'mail' && recipients.length === 0) return;
    if (runningRef.current) return;
    runningRef.current = true;
    setBusy(true);
    try {
      // Firmenkopf inkl. Briefkopftext + Briefpapier aus quoteConfig, damit das
      // versendete PDF exakt der Editor-Vorschau entspricht.
      const companyForPdf = buildCompanyForPdf(company, quoteConfig, invoiceConfig);
      // Modus 'priced': eigene Kalkulationsseiten entfallen, das bepreiste
      // Original ersetzt sie (PDF = Anschreiben + LV + Zahlungsbed./AGB).
      const mode = lvOriginalMode(quote);
      const doc = generateQuotePDF(
        quote, customer, companyForPdf, userName, userSignature,
        // Grussformel aus derselben Pflegestelle wie bei der Rechnung —
        // sonst traegt das versendete PDF einen anderen Gruss als die Rechnung.
        { skipCalculation: mode === 'priced', closingText: invoiceConfig?.closingText },
      );
      // Ist ein Original-LV eingebunden, entstehen hier andere Bytes als im
      // jsPDF-Doc. Anhang UND Archivkopie muessen aus derselben Quelle kommen,
      // sonst archivieren wir ein anderes PDF als das versendete. Im Modus
      // 'none' ist das ein No-op und liefert exakt die bisherigen Bytes.
      const pdfBytes = await applyOriginalLv(doc.output('arraybuffer'), quote);
      if (!aliveRef.current) return;

      // Archiv-Kopie ins Cloud-Storage – nicht fatal: bei Fehler lokal weitersenden.
      let archivedUrl: string | undefined;
      try {
        archivedUrl = await uploadPdf(pdfBytesToBlob(pdfBytes), filename);
      } catch (e) {
        console.warn('Angebots-PDF-Archivierung fehlgeschlagen – lokaler Versand läuft weiter.', e);
      }
      if (!aliveRef.current) return;
      setPdfUrl(archivedUrl);

      if (way === 'portal') {
        // Kein Mailentwurf: das PDF selbst wird im Portal hochgeladen. Es sind
        // dieselben Bytes wie die Archivkopie.
        await dateiAusgeben(pdfBytesToBlob(pdfBytes), filename);
      } else {
        const eml = buildQuoteEml({
          to: recipients,
          subject,
          bodyText: body,
          attachmentFilename: filename,
          attachmentBase64: pdfBytesToBase64(pdfBytes),
        });
        downloadEml(`${fileBase || `Angebot_${quote.quoteNumber}`}.eml`, eml);
      }
      if (aliveRef.current) setStep('done');
    } catch (e) {
      if (aliveRef.current) {
        alert((way === 'portal' ? 'PDF konnte nicht erstellt werden: ' : 'Entwurf konnte nicht erstellt werden: ') + rechtFehlerOder(e));
      } else {
        console.warn('Angebots-Entwurf fehlgeschlagen, Dialog war bereits geschlossen.', e);
      }
    } finally {
      runningRef.current = false;
      if (aliveRef.current) setBusy(false);
    }
  };

  const handleMarkSent = async () => {
    setMarking(true);
    try {
      const followUpDate = computeFollowUpDate(new Date().toISOString(), quoteConfig?.followUpDays ?? 14);
      const { updatedAt } = await markQuoteSent(quote.id, {
        pdfUrl: pdfUrl ?? '',
        sentTo,
        followUpDate,
        customerSnapshot: customer,
      });
      onMarkedSent({
        status: 'sent',
        sentAt: new Date().toISOString(),
        sentTo,
        pdfUrl: pdfUrl ?? '',
        followUpDate,
        customerSnapshot: customer,
        // Neuer Serveranker — der Editor setzt die naechste Aktion darauf auf.
        updatedAt,
      });
      onClose();
    } catch (e) {
      alert('Markieren fehlgeschlagen: ' + rechtFehlerOder(e));
    } finally {
      setMarking(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden animate-scale-in max-h-[90vh] flex flex-col">
        {/* Kopf */}
        <div className="bg-slate-900 text-white p-6 flex justify-between items-center shrink-0">
          <div className="flex items-center gap-3">
            <div className="bg-blue-500/20 p-2 rounded-xl border border-blue-500/30">
              <Mail className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <h3 className="font-bold text-lg">Angebot versenden</h3>
              <p className="text-xs text-slate-400">{quote.quoteNumber}</p>
            </div>
          </div>
          <button onClick={onClose} className="hover:bg-white/10 p-2 rounded-full transition-all">
            <X className="w-5 h-5" />
          </button>
        </div>

        {step === 'compose' ? (
          <>
            <div className="p-6 space-y-5 overflow-y-auto">
              {/* Weg */}
              <div className="grid grid-cols-2 gap-1 p-1 rounded-xl bg-slate-100">
                {([
                  { key: 'mail', label: 'Per E-Mail (Outlook)', icon: <Mail className="w-4 h-4" /> },
                  { key: 'portal', label: 'Vergabeportal', icon: <Upload className="w-4 h-4" /> },
                ] as const).map((w) => (
                  <button
                    key={w.key}
                    type="button"
                    onClick={() => setWay(w.key)}
                    disabled={busy}
                    aria-pressed={way === w.key}
                    className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-all disabled:opacity-60 ${
                      way === w.key ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    {w.icon}{w.label}
                  </button>
                ))}
              </div>

              {way === 'portal' ? (
                <>
                  <div className="flex items-start gap-2 p-3 rounded-xl bg-blue-50 border border-blue-200 text-blue-800 text-xs">
                    <Info className="w-4 h-4 shrink-0 mt-0.5" />
                    <span>Es geht keine E-Mail raus. Sie erhalten das Angebots-PDF zum Hochladen im Vergabeportal; eine Kopie wird archiviert.</span>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[11px] font-bold text-slate-500 uppercase tracking-widest ml-1">Vergabeportal</label>
                    <input
                      value={portalName}
                      onChange={(e) => setPortalName(e.target.value)}
                      placeholder="z. B. DTVP, eVergabe (optional)"
                      className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none"
                    />
                    <p className="text-[11px] text-slate-400 ml-1 break-all">Datei: {filename}</p>
                  </div>
                </>
              ) : (
                <>
                  {/* Empfaenger */}
                  <div className="space-y-2">
                    <label className="text-[11px] font-bold text-slate-500 uppercase tracking-widest ml-1">Empfänger *</label>
                    {candidates.length === 0 ? (
                      <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-xs">
                        <Info className="w-4 h-4 shrink-0 mt-0.5" />
                        <span>Für diesen Kunden ist keine E-Mail-Adresse hinterlegt. Bitte am Kunden oder an einem Kontakt eine E-Mail ergänzen — oder oben „Vergabeportal“ wählen.</span>
                      </div>
                    ) : (
                      <div className="space-y-1.5">
                        {candidates.map((c) => (
                          <label key={c.email} className="flex items-center gap-3 p-2.5 rounded-xl bg-slate-50 border border-slate-200 cursor-pointer hover:bg-slate-100 transition-colors">
                            <input
                              type="checkbox"
                              checked={selected.has(c.email)}
                              onChange={() => toggle(c.email)}
                              className="w-4 h-4 rounded accent-blue-600"
                            />
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-slate-700 truncate">{c.label}</div>
                              <div className="text-xs text-slate-400 truncate">{c.email}</div>
                            </div>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Betreff */}
                  <div className="space-y-1.5">
                    <label className="text-[11px] font-bold text-slate-500 uppercase tracking-widest ml-1">Betreff</label>
                    <input
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none"
                    />
                  </div>

                  {/* Text */}
                  <div className="space-y-1.5">
                    <label className="text-[11px] font-bold text-slate-500 uppercase tracking-widest ml-1">Text</label>
                    <textarea
                      value={body}
                      onChange={(e) => setBody(e.target.value)}
                      rows={7}
                      className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none resize-none"
                    />
                    <p className="text-[11px] text-slate-400 ml-1">Ihre Signatur fügt Outlook automatisch hinzu.</p>
                  </div>
                </>
              )}
            </div>

            <div className="p-5 bg-slate-50 border-t flex justify-end gap-3 shrink-0">
              <Button variant="ghost" onClick={onClose}>Abbrechen</Button>
              <Button
                variant="primary"
                onClick={handleCreate}
                disabled={busy || (way === 'mail' && recipients.length === 0)}
                className="shadow-lg border-b-2 border-blue-700"
                icon={busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              >
                {way === 'portal'
                  ? (busy ? 'Erstelle PDF…' : 'PDF herunterladen')
                  : (busy ? 'Erstelle Entwurf…' : 'Outlook-Entwurf erstellen')}
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="p-8 space-y-4 overflow-y-auto text-center">
              <div className="bg-emerald-50 p-4 rounded-full w-fit mx-auto">
                <CheckCircle2 className="w-10 h-10 text-emerald-600" />
              </div>
              {way === 'portal' ? (
                <>
                  <h4 className="font-bold text-lg text-slate-800">PDF heruntergeladen</h4>
                  <p className="text-sm text-slate-600">
                    Das PDF im Vergabeportal hochladen und das Angebot dort abgeben.
                  </p>
                  <p className="text-xs text-slate-400">
                    Anschließend können Sie das Angebot als abgegeben markieren; das Nachfassdatum wird automatisch gesetzt.
                  </p>
                </>
              ) : (
                <>
                  <h4 className="font-bold text-lg text-slate-800">Outlook-Entwurf heruntergeladen</h4>
                  <p className="text-sm text-slate-600">
                    Der Outlook-Entwurf wurde heruntergeladen — öffnen, prüfen, senden.
                  </p>
                  <p className="text-xs text-slate-400">
                    Anschließend können Sie das Angebot als versendet markieren; das Nachfassdatum wird automatisch gesetzt.
                  </p>
                </>
              )}
            </div>

            <div className="p-5 bg-slate-50 border-t flex justify-end gap-3 shrink-0">
              <Button variant="ghost" onClick={onClose}>Später</Button>
              <Button
                variant="primary"
                onClick={handleMarkSent}
                disabled={marking}
                className="shadow-lg border-b-2 border-blue-700"
                icon={marking ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              >
                {marking ? 'Speichert…' : way === 'portal' ? 'Als abgegeben markieren' : 'Als versendet markieren'}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
